'use client';
import {
  faBoxesPacking,
  faCartShopping,
  faHeart,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { FC } from 'react';
import MobileNavigationItems from '@/components/common/layout/mobile-navigation/mobile-navigation-items';
import {
  Badge,
  Button,
  Icon,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import useHeaderContent from '@/hooks/use-header-content';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { useStudioPreviewPortalRoot } from '@/providers/studio-preview-portal-provider';
import { cn } from '@/utils';
import { getShoppingCartProductsNum } from '@/utils/get-product-items-number';
import { getKeycloakLoginOptions } from '@/utils/keycloak-login';
import { studioPreviewPortalModalClassNames } from '@/utils/studio-preview-portal-modal';
import FeatureFlag from '../../../feature-flag/feature-flag';
import LanguageSelector from '../language-selector/language-selector';
import SearchInput from '../search-input/search-input';

type MobileMenuDrawerProps = {
  isOpen: boolean;
  toggleDrawer: () => void;
  logoSrc: string;
};

const MobileMenuDrawerPreview: FC<MobileMenuDrawerProps> = ({ isOpen, toggleDrawer, logoSrc }) => {
  const { t } = useTranslation();
  const { features } = useCoreConfig();
  const headerContent = useHeaderContent();
  const parsedLogoHeight = Number(headerContent.logoHeight);
  const logoHeight =
    Number.isFinite(parsedLogoHeight) && parsedLogoHeight > 0 ? parsedLogoHeight : 40;
  const resolvedLogoSrc = logoSrc || '/imgs/ibee-logo.png';
  const portalRoot = useStudioPreviewPortalRoot();
  const resolveThemeColor = useResolveThemeColor();
  const chromeBg = resolveThemeColor(
    headerContent.mainBackground ?? headerContent.background ?? 'content1',
  );
  const chromeFg = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const constrainModalToStudioPortal = portalRoot != null;

  return (
    <Modal
      aria-labelledby="modal-title"
      isOpen={isOpen}
      hideCloseButton
      size="full"
      portalContainer={portalRoot ?? undefined}
      classNames={constrainModalToStudioPortal ? studioPreviewPortalModalClassNames : undefined}
    >
      <ModalContent
        className={cn(
          'flex min-h-0 flex-col !rounded-none bg-transparent',
          constrainModalToStudioPortal && 'h-full max-h-full',
        )}
        style={{ backgroundColor: chromeBg, color: chromeFg }}
      >
        <ModalHeader className="flex items-center justify-between pr-4 text-inherit">
          <div className="flex items-center" style={{ height: `${logoHeight}px` }}>
            <RenderableImage
              src={resolvedLogoSrc}
              height={logoHeight}
              className="block h-full w-auto max-w-[220px] object-contain"
              alt="logo"
            />
          </div>
          <IconButton aria-label="Close menu" onPress={toggleDrawer}>
            <Icon icon={faClose} style={{ color: chromeFg }} />
          </IconButton>
        </ModalHeader>
        <ModalBody className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
          <div className="flex h-full flex-col gap-4 overflow-y-auto">
            <FeatureFlag feature="globalSearch">
              <SearchInput onNavigate={toggleDrawer} />
            </FeatureFlag>
            <MobileNavigationItems onNavigate={toggleDrawer} />
          </div>
        </ModalBody>
        <ModalFooter className="flex flex-col gap-0 px-6 pb-6 text-inherit">
          <FeatureFlag feature="favorites">
            <div className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium text-inherit">
              <span>{t?.common.layout.favorites}</span>
              <Badge variant="solid" content={0} size="sm">
                <Icon icon={faHeart} style={{ color: chromeFg }} />
              </Badge>
            </div>
          </FeatureFlag>
          {features.cartForGuests ? (
            <div className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium text-inherit">
              <span>{t?.common.layout.myCart}</span>
              <Badge variant="solid" content={0} size="sm">
                <Icon icon={faCartShopping} style={{ color: chromeFg }} />
              </Badge>
            </div>
          ) : null}
          <div className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium">
            <span>{t?.account?.locale ?? 'Language'}</span>
            <LanguageSelector presentation="mobileMenu" />
          </div>
          <Button className="mt-4" color="primary" isDisabled>
            {t?.common.layout.login}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const MobileMenuDrawerLive: FC<MobileMenuDrawerProps> = ({ isOpen, toggleDrawer, logoSrc }) => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const { favorites } = useFavorites();
  const { myCart } = useMyCart();
  const coreConfig = useCoreConfig();
  const { features } = coreConfig;

  const canUseCart = Boolean(keycloak?.authenticated || features.cartForGuests);

  const favoritesCount = favorites?.products?.length ?? 0;
  const cartCount = getShoppingCartProductsNum(myCart?.products);

  const headerContent = useHeaderContent();
  const parsedLogoHeight = Number(headerContent.logoHeight);
  const logoHeight =
    Number.isFinite(parsedLogoHeight) && parsedLogoHeight > 0 ? parsedLogoHeight : 40;
  const resolvedLogoSrc = logoSrc || '/imgs/ibee-logo.png';

  const portalRoot = useStudioPreviewPortalRoot();
  const resolveThemeColor = useResolveThemeColor();

  const chromeBg = resolveThemeColor(
    headerContent.mainBackground ?? headerContent.background ?? 'content1',
  );
  const chromeFg = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const constrainModalToStudioPortal = portalRoot != null;

  return (
    <>
      <Modal
        aria-labelledby="modal-title"
        isOpen={isOpen}
        hideCloseButton
        size="full"
        portalContainer={portalRoot ?? undefined}
        classNames={constrainModalToStudioPortal ? studioPreviewPortalModalClassNames : undefined}
      >
        <ModalContent
          className={cn(
            'flex min-h-0 flex-col !rounded-none bg-transparent',
            constrainModalToStudioPortal && 'h-full max-h-full',
          )}
          style={{ backgroundColor: chromeBg, color: chromeFg }}
        >
          <ModalHeader className="flex items-center justify-between pr-4 text-inherit">
            <Link href="/">
              <div className="flex items-center" style={{ height: `${logoHeight}px` }}>
                <RenderableImage
                  src={resolvedLogoSrc}
                  height={logoHeight}
                  className="block h-full w-auto max-w-[220px] cursor-pointer object-contain"
                  alt="logo"
                />
              </div>
            </Link>
            <IconButton aria-label="Close menu" onPress={toggleDrawer}>
              <Icon icon={faClose} style={{ color: chromeFg }} />
            </IconButton>
          </ModalHeader>
          <ModalBody className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <div className="flex h-full flex-col gap-4 overflow-y-auto">
              <FeatureFlag feature="globalSearch">
                <SearchInput onNavigate={toggleDrawer} />
              </FeatureFlag>
              <MobileNavigationItems onNavigate={toggleDrawer} />
            </div>
          </ModalBody>
          <ModalFooter className="flex flex-col gap-0 px-6 pb-6 text-inherit">
            {keycloak?.authenticated && (
              <>
                <Link
                  href="/orders"
                  noStyle
                  className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium text-inherit"
                  onClick={toggleDrawer}
                >
                  <span>{t?.common.layout.myOrders}</span>
                  <Icon icon={faBoxesPacking} style={{ color: chromeFg }} />
                </Link>
                <Link
                  href="/account"
                  noStyle
                  className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium text-inherit"
                  onClick={toggleDrawer}
                >
                  <span>{t?.common.layout.userProfile}</span>
                  <Icon icon={faUser} style={{ color: chromeFg }} />
                </Link>
              </>
            )}
            <FeatureFlag feature="favorites">
              <Link
                href="/favorites"
                noStyle
                className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium text-inherit"
                onClick={toggleDrawer}
              >
                <span>{t?.common.layout.favorites}</span>
                <Badge variant="solid" content={favoritesCount} size="sm">
                  <Icon icon={faHeart} style={{ color: chromeFg }} />
                </Badge>
              </Link>
            </FeatureFlag>
            {canUseCart && (
              <Link
                href="/my-cart"
                noStyle
                className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium text-inherit"
                onClick={toggleDrawer}
              >
                <span>{t?.common.layout.myCart}</span>
                <Badge variant="solid" content={cartCount} size="sm">
                  <Icon icon={faCartShopping} style={{ color: chromeFg }} />
                </Badge>
              </Link>
            )}
            <div className="flex w-full items-center justify-between border-b border-current/20 py-3 font-medium">
              <span>{t?.account?.locale ?? 'Language'}</span>
              <LanguageSelector presentation="mobileMenu" />
            </div>
            {!keycloak?.authenticated && (
              <Button
                className="mt-4"
                color="primary"
                onPress={() =>
                  keycloak?.login(getKeycloakLoginOptions({}, coreConfig.defaultLanguageCode))
                }
              >
                {t?.common.layout.login}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const MobileMenuDrawer: FC<MobileMenuDrawerProps> = props => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  if (isStudioPreview) {
    return <MobileMenuDrawerPreview {...props} />;
  }
  return <MobileMenuDrawerLive {...props} />;
};

export default MobileMenuDrawer;
